import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import "core-js/stable/array/to-spliced";
import { withUser } from "@unifiedpatents/sso";
import { PriorArtDocumentType } from '@/helpers/constants';
import { isTextSimilarByThreshold } from '@/helpers/text-analysis';
import { getApplicationParagraphNumber } from '@/helpers/get-application-paragraph-number';
import { getCitationCoordinates } from '@/helpers/get-citation-coordinates';
import { getInvalidityElementByPatent, getSummaryPatent, getShortSummary, getInfringementElementByStandardAdvanced } from '@/services/api-patty';
import { getReferenceNumbersForPatent } from '@/services/api-patents';
import { flattenClaims } from '@/helpers/converters';
import { calculateTextSimilarities } from '@/helpers/text-analysis';
import { PriorArtTypes } from '@/helpers/constants';

const PriorArtMappingContext = createContext();

export const NOT_FOUND_MESSAGE = 'The prior art does not contain sufficient details that directly relate to or invalidate the claim element'
const DOCUMENT_REFERENCE_FIELDS = [
  "application_date",
  "assignee_current",
  "assignee_original",
  "assignee_parent",
  "country",
  "cpc_class",
  "examiner",
  "expiration_date",
  "grant_date",
  "inventors",
  "is_challenged",
  "is_litigated",
  "kind_code",
  "last_challenged_at",
  "last_litigated_at",
  "num_challenged",
  "num_litigated",
  "priority_date",
  "publication_status",
  "publication_type"
];

const PriorArtMappingProviderBase = ({ children, user }) => {
  const [priorArtMappingData, setPriorArtMappingData] = useState({
    targetPatent: {}, // The patent currently being analyzed.
    targetPatentSummary: '', // Summary of the target patent.
    targetPatentSummaryLoading: false, // Indicates if the target patent summary is currently being loaded.
    targetPatentSummaryReceived: true, // Flag to check if the target patent summary has been received. Needed because we use websocket for retrieving summary.
    targetElements: [], // Elements within the target patent for analysis.
    claimConstructions: [], // Array to store user interpretations or definitions for claim element terms.
    priorArtList: [], // Maintains the order of selected prior art documents. Used as keys to access corresponding metadata in 'priorArts' and results in 'claimChartResults'.
    priorArts: {}, // Metadata of prior art , keyed by publication number or title.
    claimChartResults: {
      // Contains results of mapping target elements to prior art excerpts.
      // Each key corresponds to a prior art document, and its value is an object with:
      // - title: The title or identifier of the prior art.
      // - priorArtSummary: Summary of the prior art document.
      // - priorArtSummaryLoading: Indicates if the prior art summary is currently being loaded.
      // - priorArtSummaryReceived: Flag to check if the prior art summary has been received.
      // - mappedElements: Array of objects, each representing a target element mapped to prior art excerpts.
      //   Each object contains:
      //   - responses: Array of response objects, each with:
      //     - text: Array of dictionaries with 'value' (text) and 'language'.
      //     - extendedContext: Object with 'value' (context text), 'citation', and 'citationCoordinates'.
      //     - showExtendedContext: Boolean indicating if extended context should be shown.
      //     - score: String representing the similarity score.
      //     - citation: String representing the citation.
      //     - citationCoordinates: Array of coordinates for the citation.
      //     - citationLoading: Boolean indicating if citation data is loading.
      //     - isPinned: Boolean indicating if the response is pinned.
      //     - isUserAdded: Boolean indicating if the response was added by the user.
      //   - mappedElementLoading: Boolean indicating if the element mapping is loading.
      // - dislikedMappedElements: Array to track mapped elements that users dislike.
    },
    showExtendedContextForAll: false,
    showMoreQuotesForALL: false,
  });

  const showExtendedContextForAllRef = useRef(priorArtMappingData.showExtendedContextForAll);

  // Results from our `patRef` model that text with column and line numerals for each line
  const grantNumbers = Object.values(priorArtMappingData.priorArts)
    .filter(priorArt => priorArt.isCitationCoordinateSupport)
    .map(priorArt => priorArt.grant_number)
    .filter(Boolean);
  const [patRefOutputs, setPatRefOutputs] = useState({});

  const [responsesUpdatedIndex, setResponsesUpdatedIndex] = useState({ priorArtKey: null, rowKey: null, timestamp: Date.now() }); // flag to trigger getCitation once response is updated

  const prevPatRefOutputsRef = useRef({});

  // The purpose for this function is to convert the patent metadata (patent.assets) into a flat text, which could be used as the reference
  // for generating the summary. For example, this will allow the summary to point out the filing date, Assignee, Inventors...
  const getConcatenatedPatentInfo = (assets) => {
    if (!assets) return '';
    // Check if 'assets' object directly contains the property named 'field'
    // This avoids false positives from inherited properties and ensures we're working with 'assets' own data
    return DOCUMENT_REFERENCE_FIELDS.filter(field => Object.hasOwn(assets, field))
      .map(field => {
        const value = assets[field];
        if (Array.isArray(value)) {
          return `${field}: ${value.join(', ')}`;
        }
        return `${field}: ${value}`;
      }).join(', ');
  };

  const updateTargetPatent = (targetPatent) => {
    setPriorArtMappingData((prev) => ({ ...prev, targetPatent }));
  };

  const updateClaimConstructionsByIndex = (index, claimConstruction) => {
    setPriorArtMappingData(prev => ({
      ...prev,
      claimConstructions: prev.claimConstructions.toSpliced(index, 1, claimConstruction)
    }));
  };

  const updateTargetElements = (targetElements) => {
    setPriorArtMappingData(prev => ({
      ...prev,
      targetElements,
      claimConstructions: targetElements.map(() => ''),
    }));
  };

  const updatePriorArt = (priorArtList) => {
    const keys = priorArtList.map(priorArt => priorArt.publication_number || priorArt?.title || 'Unknown');

    const priorArtDictionary = priorArtList.reduce((acc, priorArt) => {
      const key = priorArt.publication_number || priorArt?.title || 'Unknown';

      const isTranslationSupport = priorArt?.defaultLanguage !== "EN" && 
             priorArt?.translations?.some(translation => translation.lang === "EN");
      const isCitationSupport = ['US', 'KR', 'JP', 'DE', 'WO', 'EP', 'CN'].includes(priorArt?.country);
      const citationSupportType = isCitationSupport 
        ? priorArt?.country === 'US'
            ? priorArt?.grant_number?.length >= 9
                ? PriorArtDocumentType.APPLICATION
                : PriorArtDocumentType.GRANT
            : PriorArtDocumentType.INTERNATIONAL
        : null;
      const isCitationCoordinateSupport = isCitationSupport && 
        (citationSupportType === PriorArtDocumentType.GRANT || 
         citationSupportType === PriorArtDocumentType.APPLICATION);

      acc[key] = {
        ...priorArt,
        isTranslationSupport,
        isCitationSupport,
        citationSupportType,
        isCitationCoordinateSupport
      };
      return acc;
    }, {});

    const claimChartResults = keys.reduce((acc, key) => {
      acc[key] = {
        title: key,
        priorArtSummary: '',
        priorArtSummaryLoading: false,
        priorArtSummaryReceived: true,
        mappedElements: priorArtMappingData.targetElements.map(() => ({
          responses: [],
          mappedElementLoading: false
        })),
        dislikedMappedElements: [],
      };
      return acc;
    }, {});

    setPriorArtMappingData(prevData => ({
      ...prevData,
      priorArtList: keys,
      priorArts: priorArtDictionary,
      claimChartResults,
    }));
  };

  const setMappedElementsByKey = (key, index, newData) => {
    setPriorArtMappingData(prev => {
      const updatedClaimChartResults = { ...prev.claimChartResults };
      if (updatedClaimChartResults[key]) {
        const updatedMappedElements = updatedClaimChartResults[key].mappedElements.toSpliced(index, 1, {
          ...updatedClaimChartResults[key].mappedElements[index],
          ...newData
        });
        updatedClaimChartResults[key].mappedElements = updatedMappedElements;
      }
      return {
        ...prev,
        claimChartResults: updatedClaimChartResults
      };
    });
  };

  const updateDislikedMappedElementsByKey = (key, index, newDislikedMappedElement) => {
    setPriorArtMappingData(prev => {
      const updatedClaimChartResults = { ...prev.claimChartResults };
      if (updatedClaimChartResults[key]) {
        const existingDislikedElements = updatedClaimChartResults[key].dislikedMappedElements[index] || [];
        const updatedDislikedElements = existingDislikedElements.concat(newDislikedMappedElement);
        updatedClaimChartResults[key].dislikedMappedElements = updatedClaimChartResults[key].dislikedMappedElements.toSpliced(index, 1, updatedDislikedElements);

        const elementData = updatedClaimChartResults[key].mappedElements[index];
        const filteredResponses = elementData.responses.filter(response => {
          return !updatedDislikedElements.some(disliked => 
            response.text.some(textObj => textObj.value === disliked)
          );
        });

        const finalResponses = filteredResponses.length > 0 
          ? filteredResponses 
          : [{ text: [{ value: NOT_FOUND_MESSAGE, language: 'EN' }], extendedContext: null, score: 'None', citation: '', citationLoading: false, isPinned: false, isUserAdded: false }];

        setMappedElementsByKey(key, index, { responses: finalResponses });
      }
      return {
        ...prev,
        claimChartResults: updatedClaimChartResults
      };
    });
  }; 

  // Sort responses: prioritize pinned responses
  const sortResponses = (responses) => {
    return responses.sort((a, b) => {
      if (b.isPinned !== a.isPinned) {
        return b.isPinned - a.isPinned;
      }
      if (a.isUserAdded !== b.isUserAdded) {
        return b.isUserAdded - a.isUserAdded;
      }
    });
  };

  const formatColumnAndLineData = (data) => {
    if (!data) return;

    if (data.citation === 'Abstract') {
      return 'Abstract';
    }

    let text;
    if (data.startColumn === data.endColumn) {
      if (data.startLine === data.endLine) text = `Col. ${data.startColumn}, Ln. ${data.startLine}`;
      else text = `Col. ${data.startColumn}, Ln. ${data.startLine} - ${data.endLine}`;
    } else {
      text = `Col. ${data.startColumn}, Ln. ${data.startLine} - Col. ${data.endColumn}, Ln. ${data.endLine}`;
    }
    return text;
  }

  const findCorrespondingTranslatedParagraph = (key, paragraphNumber) => {
    const priorArt = priorArtMappingData.priorArts[key];

    if (!priorArt || !priorArt.translations) return null;

    // Find the corresponding English translation
    if (paragraphNumber === 'Abstract') { 
      const englishTranslation = priorArt.abstractTranslations?.find(t => t.lang === 'EN');
      if (!englishTranslation || !englishTranslation.text) return null;
      return englishTranslation.text;
    } else {
      const englishTranslation = priorArt.translations?.find(t => t.lang === 'EN');
      if (!englishTranslation || !englishTranslation.text) return null;

      // remove the paragraph number at the start of the text
      const correspondingParagraph = englishTranslation.text[paragraphNumber].replace(/^\[\d+\]\s*/, '');
      return correspondingParagraph || null;
    }
  };

  const updateInfringementMappedElement = async (key, elementIndex) => {
    const priorArt = priorArtMappingData.priorArts[key];
    const elementData = priorArtMappingData.claimChartResults[key].mappedElements[elementIndex];

    if (elementData?.mappedElementLoading) {
      return;
    }

    // Mark as loading before starting the async operation
    const existingPinnedResponses = elementData?.responses.filter(res => res.isPinned) || [];
    setMappedElementsByKey(key, elementIndex, { responses: existingPinnedResponses, mappedElementLoading: true });
    const payload = {
      claim_full_text: priorArtMappingData.targetElements[elementIndex]['fullClaimText'],
      claim_element_text: priorArtMappingData.targetElements[elementIndex]['text'],
      user_id: user.profile.user_id,
    };
    const { quotedStrings, claimElementEmbedding } = await getInfringementElementByStandardAdvanced(priorArt.standardName, payload);

    const otherEmbeddings = quotedStrings.map(element => element.embeddings);
    const scores = await calculateTextSimilarities(claimElementEmbedding, otherEmbeddings);

    const processedElements = quotedStrings.map((element, index) => {
      return {
        text: [{ value: element.quotedString, language: 'EN' }],
        extendedContext: { value: element.sectionFullText, citation: element.title },
        showExtendedContext: showExtendedContextForAllRef.current,
        score: scores[index],
        citation: element.title,
        citationCoordinates: [],
        citationLoading: false,
        isPinned: false,
        isUserAdded: false 
      };
    });
    const sortedResponses = sortResponses(processedElements);
    // Check if both new and pinned responses are empty
    const finalResponses = existingPinnedResponses.length > 0 || sortedResponses.length > 0
      ? [...existingPinnedResponses, ...sortedResponses]
      : [{ text: [{ value: NOT_FOUND_MESSAGE, language: 'EN' }], extendedContext: { value: NOT_FOUND_MESSAGE, citation: '', citationCoordinates: [] }, showExtendedContext: false, score: 'None', citation: '', citationCoordinates: [], citationLoading: false, isPinned: false, isUserAdded: false }];

    // Update the responses and mark as not loading
    setMappedElementsByKey(key, elementIndex, { responses: finalResponses, mappedElementLoading: false });
  }

  const updateInvalidityMappedElement = async (key, elementIndex) => {
    const elementData = priorArtMappingData.claimChartResults[key].mappedElements[elementIndex];
    const priorArt = priorArtMappingData.priorArts[key];

    const priorArtAbstract = priorArt.abstract || '';
    const priorArtClaims = priorArt.claims ? flattenClaims(priorArt.claims) : [];
    const priorArtDescription = priorArt.description || '';

    if (elementData?.mappedElementLoading) {
      return;
    }

    // Mark as loading before starting the async operation
    const existingPinnedResponses = elementData?.responses.filter(res => res.isPinned) || [];
    setMappedElementsByKey(key, elementIndex, { responses: existingPinnedResponses, mappedElementLoading: true });

    try {
      const payload = {
        challenged_patent: priorArtMappingData.targetPatent?.publication_number,
        challenged_claim: priorArtMappingData.targetElements[elementIndex]['text'],
        claim_construction: priorArtMappingData.claimConstructions[elementIndex],
        user_id: user.profile.user_id,
        get_more_quotes: true,
        docs: [
          priorArtAbstract,
          priorArt.defaultLanguage === 'EN' ? priorArtClaims.map(claim => claim.text).join('\n') : "",
          priorArtDescription,
        ],
      };

      const { quotedStrings } = await getInvalidityElementByPatent(payload);

      // Processing elements and adding both original and translated text as an array of objects
      const processedElements = quotedStrings.map(element => {
        const texts = [{ value: element.text, language: priorArt.defaultLanguage || 'EN' }];
        return texts;
      });

      const responsesWithScoreAndTranslation = await Promise.all(processedElements.map(async (texts, index) => {
        const responseText = texts.find(text => text.language === 'EN')?.value || texts[0].value;
        const { paragraphNumber, extendedContext } = await getApplicationParagraphNumber(priorArtAbstract, priorArtClaims, priorArtDescription, responseText);

        if (priorArt.isTranslationSupport) {
          let translatedText = '';
          if (paragraphNumber) {
            //eslint-disable-next-line
            translatedText = findCorrespondingTranslatedParagraph(key, paragraphNumber.replace(/[\[\]]/g, ''));
          }

          if (translatedText) {
            texts.push({ value: translatedText, language: 'EN' });
          }
        }

        return {
          text: texts,
          extendedContext: { value: extendedContext, citation: (priorArt.citationSupportType === PriorArtDocumentType.APPLICATION || priorArt.citationSupportType === PriorArtDocumentType.INTERNATIONAL) ? paragraphNumber : '' },
          showExtendedContext: showExtendedContextForAllRef.current,
          score: quotedStrings[index].similarity,
          citation: (priorArt.citationSupportType === PriorArtDocumentType.APPLICATION || priorArt.citationSupportType === PriorArtDocumentType.INTERNATIONAL) ? paragraphNumber : '',
          citationCoordinates: [],
          citationLoading: priorArt.citationSupportType === PriorArtDocumentType.APPLICATION || priorArt.citationSupportType === PriorArtDocumentType.GRANT,
          isPinned: false,
          isUserAdded: false,
        };
      }));

      // Generate new response entries from filtered results and exclude any responses
      // that are similar to already pinned responses or already disliked. This ensures that new responses
      // are relevant and do not duplicate important information already considered.
      const newResponses = responsesWithScoreAndTranslation.filter(response => 
        !existingPinnedResponses.some(pinned => {
          const { isSimilar } = isTextSimilarByThreshold(response.text[0].value, pinned.text[0].value);
          return isSimilar;
        }) && !(priorArtMappingData.claimChartResults[key].dislikedMappedElements[elementIndex] || []).includes(response.text[0].value)
      );

      const sortedResponses = sortResponses(newResponses);

      // Check if both new and pinned responses are empty
      const finalResponses = existingPinnedResponses.length > 0 || sortedResponses.length > 0
        ? [...existingPinnedResponses, ...sortedResponses]
        : [{ text: [{ value: NOT_FOUND_MESSAGE, language: 'EN' }], extendedContext: { value: NOT_FOUND_MESSAGE, citation: '', citationCoordinates: [] }, showExtendedContext: false, score: 'None', citation: '', citationCoordinates: [], citationLoading: false, isPinned: false, isUserAdded: false }];

      // Update the responses and mark as not loading
      setMappedElementsByKey(key, elementIndex, { responses: finalResponses, mappedElementLoading: false });
      setResponsesUpdatedIndex({ priorArtKey: key, rowKey: elementIndex, timestamp: Date.now() });
    } catch (error) {
      // Handle error by setting a fallback message and marking as not loading
      const defaultResponse = existingPinnedResponses.length > 0 
        ? existingPinnedResponses 
        : [{ text: [{ value: NOT_FOUND_MESSAGE, language: 'EN' }], extendedContext: { value: NOT_FOUND_MESSAGE, citation: '', citationCoordinates: [] }, showExtendedContext: false, score: 'None', citation: '', citationCoordinates: [], citationLoading: false, isPinned: false, isUserAdded: false }];
      setMappedElementsByKey(key, elementIndex, { responses: defaultResponse, mappedElementLoading: false });
    }
  };

  const updateMappedElement = async (key, elementIndex) => {
    const priorArt = priorArtMappingData.priorArts[key];
    if (priorArt.type === PriorArtTypes.STANDARD) {
      await updateInfringementMappedElement(key, elementIndex);
    } else {
      await updateInvalidityMappedElement(key, elementIndex);
    }
  }

  const togglePinResponseByKey = (key, elementIndex, responseIndex) => {
    setPriorArtMappingData(prev => {
      const updatedClaimChartResults = { ...prev.claimChartResults };
      if (updatedClaimChartResults[key]) {
        const targetResponses = updatedClaimChartResults[key].mappedElements[elementIndex].responses;
        const targetResponse = targetResponses[responseIndex];

        targetResponse.isPinned = !targetResponse.isPinned;

        const sortedResponses = sortResponses(targetResponses);

        setMappedElementsByKey(key, elementIndex, { responses: sortedResponses });
      }
      return {
        ...prev,
        claimChartResults: updatedClaimChartResults
      };
    });
  };

  const toggleShowExtendedContextByKey = (key, elementIndex, responseIndex, showExtendedContext) => {
    setPriorArtMappingData(prev => {
      const updatedClaimChartResults = { ...prev.claimChartResults };
      if (updatedClaimChartResults[key]) {
        const targetResponse = updatedClaimChartResults[key].mappedElements[elementIndex].responses[responseIndex];

        targetResponse.showExtendedContext = showExtendedContext !== undefined 
          ? showExtendedContext 
          : !targetResponse.showExtendedContext;

        setMappedElementsByKey(key, elementIndex, { responses: updatedClaimChartResults[key].mappedElements[elementIndex].responses });
      }
      return {
        ...prev,
        claimChartResults: updatedClaimChartResults
      };
    });
  };

  const toggleShowExtendedContextForAll = () => {
    const newShowExtendedContextForAll = !priorArtMappingData.showExtendedContextForAll;
    setPriorArtMappingData(prev => ({
      ...prev,
      showExtendedContextForAll: newShowExtendedContextForAll,
    }));

    priorArtMappingData.priorArtList.forEach(key => {
      const claimChartResult = priorArtMappingData.claimChartResults[key];
      if (claimChartResult) {
        claimChartResult.mappedElements.forEach((element, index) => {
          const updatedResponses = element.responses.map(response => ({
            ...response,
            showExtendedContext: newShowExtendedContextForAll
          }));
          const { mappedElementLoading } = element;
          setMappedElementsByKey(key, index, { responses: updatedResponses, mappedElementLoading });
        });
      }
    });
  };

  const toggleShowMoreQuotesForALL = () => {
    setPriorArtMappingData(prev => ({
      ...prev,
      showMoreQuotesForALL: !prev.showMoreQuotesForALL,
    }));
  };

  const updateMappedElements = async () => {
    await Promise.all(
      priorArtMappingData.priorArtList.map(async (key) => {
        const targetElements = priorArtMappingData.targetElements;
        await Promise.all(
          targetElements.map((_, index) => updateMappedElement(key, index))
        );
      })
    );
  };

  const getPatentSummary = () => {
    setPriorArtMappingData(prev => ({
      ...prev,
      targetPatentSummary: '',
      targetPatentSummaryLoading: true,
      targetPatentSummaryReceived: false,
    }));

    if (priorArtMappingData.targetPatent?.description) {
      const targetPatentSummaryPayload = {
        patent_num: priorArtMappingData.targetPatent?.publication_number,
        challenged_claim: priorArtMappingData.targetElements.map(item => item.text).join(' '),
        user_id: user.profile.user_id,
        docs: [getConcatenatedPatentInfo(priorArtMappingData?.targetPatent), priorArtMappingData.targetPatent?.description],
      };
    
      const summaryCallback = data => {
        setPriorArtMappingData(prev => ({
          ...prev,
          targetPatentSummary: prev.targetPatentSummary + data,
          targetPatentSummaryLoading: false,
        }));
      };

      const summaryDoneCallback = () => {
        setPriorArtMappingData(prev => ({
          ...prev,
          targetPatentSummaryReceived: true
        }));
      };

      if (priorArtMappingData.targetPatent?.publication_number) {
        getSummaryPatent(targetPatentSummaryPayload, summaryCallback, summaryDoneCallback, true);
      } else {
        getShortSummary(targetPatentSummaryPayload, summaryCallback, summaryDoneCallback);
      }
    } else {
      setPriorArtMappingData(prev => ({
        ...prev,
        targetPatentSummary: '',
        targetPatentSummaryLoading: false,
        targetPatentSummaryReceived: true,
      }));
    }

    priorArtMappingData.priorArtList.map(async (key) => {
      const priorArt = priorArtMappingData.priorArts[key];
      if (priorArt.description) {
        setPriorArtMappingData(prev => {
          const updatedClaimChartResults = { ...prev.claimChartResults };
          if (updatedClaimChartResults[key]) {
            updatedClaimChartResults[key].priorArtSummary = '';
            updatedClaimChartResults[key].priorArtSummaryLoading = true;
            updatedClaimChartResults[key].priorArtSummaryReceived = false;
          }
          return {
            ...prev,
            claimChartResults: updatedClaimChartResults
          };
        });

        const priorArtSummaryPayload = {
          patent_num: key,
          user_id: user.profile.user_id,
          docs: [getConcatenatedPatentInfo(priorArtMappingData.priorArts[key]), priorArtMappingData.priorArts[key].description],
        };

        getSummaryPatent(priorArtSummaryPayload, data => {
          setPriorArtMappingData(prev => {
            const updatedClaimChartResults = { ...prev.claimChartResults };
            if (updatedClaimChartResults[key]) {
              updatedClaimChartResults[key].priorArtSummary += data;
              updatedClaimChartResults[key].priorArtSummaryLoading = false;
            }
            return {
              ...prev,
              claimChartResults: updatedClaimChartResults
            };
          });
        }, () => setPriorArtMappingData(prev => {
          const updatedClaimChartResults = { ...prev.claimChartResults };
          if (updatedClaimChartResults[key]) {
            updatedClaimChartResults[key].priorArtSummaryReceived = true;
          }
          return {
            ...prev,
            claimChartResults: updatedClaimChartResults
          };
        }), false);
      }
    });
  }

  const getCitationForText = async (key, text) => {
    const priorArt = priorArtMappingData.priorArts[key];
    const priorArtAbstract = priorArt.abstract || '';
    const priorArtClaims = priorArt.claims ? flattenClaims(priorArt.claims) : [];
    const priorArtDescription = priorArt.description || '';
    const grantNumber = priorArt.grant_number;

    const patRefOutput = patRefOutputs[grantNumber];

    if (priorArt.citationSupportType === PriorArtDocumentType.APPLICATION) {
      const { paragraphNumber } = await getApplicationParagraphNumber(priorArtAbstract, priorArtClaims, priorArtDescription, text);
      const citationCoordinates = await getCitationCoordinates(patRefOutput, priorArtAbstract, [text], !!patRefOutput?.ended_at);
      return {
        citation: paragraphNumber,
        citationCoordinates: citationCoordinates[0]?.coordinates || [],
        citationLoading: patRefOutput?.ended_at ? false : !citationCoordinates[0],
      };
    } else if (priorArt.citationSupportType === PriorArtDocumentType.GRANT) {
      const citation = await getCitationCoordinates(patRefOutput, priorArtAbstract, [text], !!patRefOutput?.ended_at);
      return {
        citation: formatColumnAndLineData(citation[0]) || '',
        citationCoordinates: citation[0]?.coordinates || [],
        citationLoading: patRefOutput?.ended_at ? false : !citation[0],
      };
    } else if (priorArt.citationSupportType === PriorArtDocumentType.INTERNATIONAL) {
      const { paragraphNumber } = await getApplicationParagraphNumber(priorArtAbstract, priorArtClaims, priorArtDescription, text);
      return {
        citation: paragraphNumber,
        citationCoordinates: [],
        citationLoading: false,
      };
    } else {
      return {
        citation: '',
        citationCoordinates: [],
        citationLoading: false,
      };
    }
  };

  const updateMappedElementCitation = async (key, elementIndex) => {
    const priorArt = priorArtMappingData.priorArts[key];
    const elementData = priorArtMappingData.claimChartResults[key].mappedElements[elementIndex];
    const grantNumber = priorArt.grant_number;
    const patRefOutput = patRefOutputs[grantNumber];

    if (elementData.mappedElementLoading || !priorArt.isCitationSupport || !priorArt.isCitationCoordinateSupport) return;
    if (priorArt.isCitationCoordinateSupport && !patRefOutput) return;
    if (elementData.responses.length === 0 || elementData.responses[0].text[0].value === NOT_FOUND_MESSAGE) return;

    // Collect all response texts
    const responseTexts = elementData.responses.map(response => response.text[0].value);
    const extendedTexts = elementData.responses.map(response => response.extendedContext.value);

    // Combine responseTexts and extendedTexts into a single array
    const allTexts = [...responseTexts, ...extendedTexts];

    // Make a single API call with the combined texts
    const allCitations = await getCitationCoordinates(patRefOutput, priorArt.abstract, allTexts, !!patRefOutput?.ended_at);

    // Split the results back into response and extended context citations
    const responseCitations = allCitations.slice(0, responseTexts.length);
    const extendedContextCitations = allCitations.slice(responseTexts.length);

    const updatedResponses = elementData.responses.map((response, index) => ({
      ...response,
      extendedContext: {
        value: extendedTexts[index],
        citation: priorArt.citationSupportType === PriorArtDocumentType.GRANT ? formatColumnAndLineData(extendedContextCitations[index]) : response.extendedContext.citation || '',
        citationCoordinates: extendedContextCitations[index]?.coordinates || [],
      },
      citation: priorArt.citationSupportType === PriorArtDocumentType.GRANT ? formatColumnAndLineData(responseCitations[index]) : response.citation || '',
      citationCoordinates: responseCitations[index]?.coordinates || [],
      citationLoading: patRefOutput?.ended_at ? false : !responseCitations[index],
    }));

    setMappedElementsByKey(key, elementIndex, { responses: updatedResponses, mappedElementLoading: false });
  };

  const addNewResponseByKey = async (key, index, newResponseText) => {
    const priorArt = priorArtMappingData.priorArts[key];

    if (!priorArt.isCitationSupport) {
      setMappedElementsByKey(key, index, {
        responses: [
          ...priorArtMappingData.claimChartResults[key].mappedElements[index].responses,
          {
            text: [{ value: newResponseText, language: 'EN' }],
            extendedContext: { loading: false, value: '', citation: '', citationCoordinates: [] },
            showExtendedContext: showExtendedContextForAllRef.current,
            score: 'None',
            citation: '',
            citationCoordinates: [],
            citationLoading: false,
            isPinned: false,
            isUserAdded: true
          }
        ]
      });
      return;
    }
  
    setMappedElementsByKey(key, index, {
      responses: [
        ...priorArtMappingData.claimChartResults[key].mappedElements[index].responses,
        {
          text: [{ value: newResponseText, language: 'EN' }],
          extendedContext: { loading: true },
          showExtendedContext: showExtendedContextForAllRef.current,
          score: 'None',
          citation: '',
          citationCoordinates: [],
          citationLoading: true,
          isPinned: false,
          isUserAdded: true
        }
      ]
    });
  
    const priorArtAbstract = priorArt.abstract || '';
    const priorArtClaims = priorArt.claims ? flattenClaims(priorArt.claims) : [];
    const priorArtDescription = priorArt.description || '';
    const { extendedContext } = await getApplicationParagraphNumber(priorArtAbstract, priorArtClaims, priorArtDescription, newResponseText);
    const citation = await getCitationForText(key, newResponseText);
    const extendedContextCitation = await getCitationForText(key, extendedContext);
  
    setMappedElementsByKey(key, index, {
      responses: priorArtMappingData.claimChartResults[key].mappedElements[index].responses.map((response, responseIndex) => {
        if (responseIndex === priorArtMappingData.claimChartResults[key].mappedElements[index].responses.length - 1) {
          return {
            ...response,
            extendedContext: { value: extendedContext, citation: extendedContextCitation.citation, citationCoordinates: extendedContextCitation.citationCoordinates },
            citation: citation.citation,
            citationCoordinates: citation.citationCoordinates,
            citationLoading: citation.citationLoading
          };
        }
        return response;
      })
    });
  };

  useEffect(() => {
    const fetchPatentReferenceForGrantNumber = async (grantNumber) => {
      const result = await getReferenceNumbersForPatent(grantNumber);
      setPatRefOutputs(prev => ({
        ...prev,
        [grantNumber]: result,
      }));

      // If 'ended_at' is not present, refetch after 20 seconds (polling)
      if (!result.ended_at) {
        setTimeout(() => fetchPatentReferenceForGrantNumber(grantNumber), 20000);
      }
    };

    if (grantNumbers && grantNumbers.length > 0) {
      grantNumbers.forEach(grantNumber => {
        fetchPatentReferenceForGrantNumber(grantNumber);
      });
    }
  }, [priorArtMappingData.targetPatent, priorArtMappingData.priorArts]);

  useEffect(() => {
    const prevPatRefOutputs = prevPatRefOutputsRef.current;
    priorArtMappingData.priorArtList.forEach(key => {
      const claimChartResult = priorArtMappingData.claimChartResults[key];
      const priorArt = priorArtMappingData.priorArts[key];
      const grantNumber = priorArt.grant_number;  
      // Check if the patRefOutputs for this key have changed
      if (priorArt && priorArt.isCitationSupport) {
        const currentOutputs = patRefOutputs[grantNumber] || [];
        const previousOutputs = prevPatRefOutputs[grantNumber] || [];
        if (currentOutputs?.pages?.length !== previousOutputs?.pages?.length) {
          claimChartResult.mappedElements.forEach((_, index) => {
            updateMappedElementCitation(key, index);
          });
        }
      }
    });

    // Update the ref with the current patRefOutputs
    prevPatRefOutputsRef.current = { ...patRefOutputs };
  }, [patRefOutputs]);

  useEffect(() => {
    if (responsesUpdatedIndex.priorArtKey !== null && responsesUpdatedIndex.rowKey !== null) {
      const priorArt = priorArtMappingData.priorArts[responsesUpdatedIndex.priorArtKey];
      if (priorArt && priorArt.isCitationSupport) {
        updateMappedElementCitation(responsesUpdatedIndex.priorArtKey, responsesUpdatedIndex.rowKey);
      }
    }
  }, [responsesUpdatedIndex]);

  useEffect(() => {
    showExtendedContextForAllRef.current = priorArtMappingData.showExtendedContextForAll;
  }, [priorArtMappingData.showExtendedContextForAll]);

  const getResult = async () => {
    getPatentSummary();
    await updateMappedElements();
  };

  const restorePriorArtMappingData = (data) => {
    setPriorArtMappingData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  return (
    <PriorArtMappingContext.Provider value={{
      priorArtMappingData,
      restorePriorArtMappingData,
      patRefOutputs,
      updateTargetPatent,
      updateTargetElements,
      updateClaimConstructionsByIndex,
      updateDislikedMappedElementsByKey,
      updatePriorArt,
      getResult,
      addNewResponseByKey,
      updateMappedElement,
      togglePinResponseByKey,
      toggleShowExtendedContextByKey,
      toggleShowExtendedContextForAll,
      toggleShowMoreQuotesForALL,
    }}>
      {children}
    </PriorArtMappingContext.Provider>
  );
};

const PriorArtMappingProvider = withUser(PriorArtMappingProviderBase);

export { PriorArtMappingProvider };

export const usePriorArtMapping = () => useContext(PriorArtMappingContext);
